import { TOAST_SEVERITY, useToast } from '@src/app/notifications/toast';
import { useAuthentication } from '@src/features/auth';
import { NotificationsIcon } from '@src/shared/ui/icons';
import { useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  pruneSeenOpenAlertIds,
  readSeenOpenAlertIds,
  subscribeToSeenOpenAlertIds,
} from '../lib';
import { acknowledgeAlertNotifications, useAlertSummary } from '../model';

export default function NewAlertNotificationController() {
  const { t } = useTranslation();
  const { dismiss, show } = useToast();
  const { user } = useAuthentication();
  const isAlertsPage = useRouterState({
    select: (state) => state.location.pathname.startsWith('/alerts'),
  });
  const userId = user?.id;
  const { data } = useAlertSummary(userId);
  const [seenOpenAlertIds, setSeenOpenAlertIds] = useState<number[]>(() =>
    userId === undefined ? [] : readSeenOpenAlertIds(userId),
  );
  const [isDocumentVisible, setIsDocumentVisible] = useState(
    () => document.visibilityState === 'visible',
  );
  const toastIdRef = useRef<string | null>(null);
  const actionableOpenAlertIds = data?.actionable_open_alert_ids;
  const acknowledge = useCallback(
    () => acknowledgeAlertNotifications(userId, actionableOpenAlertIds),
    [actionableOpenAlertIds, userId],
  );
  const hasNewActionableAlerts = useMemo(() => {
    if (userId === undefined || actionableOpenAlertIds === undefined) {
      return false;
    }

    const seenIds = new Set(seenOpenAlertIds);
    return actionableOpenAlertIds.some((id) => !seenIds.has(id));
  }, [actionableOpenAlertIds, seenOpenAlertIds, userId]);

  useEffect(() => {
    if (userId === undefined) {
      return;
    }

    const refreshSeenIds = () => {
      setSeenOpenAlertIds(readSeenOpenAlertIds(userId));
    };
    refreshSeenIds();
    return subscribeToSeenOpenAlertIds(userId, refreshSeenIds);
  }, [userId]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsDocumentVisible(document.visibilityState === 'visible');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (userId !== undefined && actionableOpenAlertIds !== undefined) {
      pruneSeenOpenAlertIds(userId, actionableOpenAlertIds);
    }
  }, [actionableOpenAlertIds, userId]);

  useEffect(() => {
    if (isAlertsPage && isDocumentVisible) {
      acknowledge();
    }
  }, [acknowledge, isAlertsPage, isDocumentVisible]);

  useEffect(() => {
    if (isAlertsPage || !hasNewActionableAlerts) {
      if (toastIdRef.current) {
        dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
      return;
    }

    if (toastIdRef.current) {
      return;
    }

    toastIdRef.current = show(
      t('alerts.new_alert_notification', 'New Alert!'),
      TOAST_SEVERITY.WARNING,
      {
        duration: 0,
        dismissOnClick: true,
        onClick: () => acknowledge(),
        icon: <NotificationsIcon color="inherit" />,
      },
    );
  }, [acknowledge, dismiss, hasNewActionableAlerts, isAlertsPage, show, t]);

  useEffect(
    () => () => {
      if (toastIdRef.current) {
        dismiss(toastIdRef.current);
      }
    },
    [dismiss],
  );

  return null;
}
